
import { parseAnkiQuestion, validateQuestionQuality } from '@/utils/anki-utils';
import { supabase } from '@/lib/supabase';

export async function deleteExistingAnkiQuestions(subject: string, importDate?: Date, timePeriod?: string) {
  try {
    let query = supabase
      .from('questions')
      .delete()
      .eq('Category', subject)
      .eq('source', 'anki');
    if (importDate && subject === 'Current Affairs') {
      const dateStr = importDate.toISOString().split('T')[0];
      const { error: caError } = await supabase
        .from('current_affairs_questions')
        .delete()
        .eq('date', dateStr)
        .eq('time_period', timePeriod || 'daily')
        .is('created_by', null) // Use IS NULL instead of eq('created_by', 'anki')
        .filter('metadata', 'cs', '{"source":"anki"}'); // Fix: use proper containment operator for JSONB
      if (caError) {
        console.error('Error deleting existing Current Affairs questions by date:', caError);
        throw caError;
      }
      console.log(`Deleted existing Current Affairs Anki questions for date: ${dateStr} and time period: ${timePeriod || 'daily'}`);
    } else {
      const { error } = await query;
      if (error) {
        console.error('Error deleting existing Anki questions:', error);
        throw error;
      }
      console.log(`Deleted existing Anki questions for subject: ${subject}`);
    }
    return true;
  } catch (error) {
    console.error('Failed to delete existing Anki questions:', error);
    throw error;
  }
}

export async function importAnkiQuestions(
  file: File,
  subject: string,
  importDate?: Date,
  timePeriod?: string
) {
  try {
    // Delete existing questions
    await deleteExistingAnkiQuestions(subject, importDate, timePeriod);
    
    const content = await file.text();
    const lines = content.split('\n').filter(line => line.trim().length > 0);
    const questions = [];
    const currentAffairsQuestions = [];
    const errors = [];
    const duplicateCheck = new Set();
    const rejectionReasons: Record<string, number> = {};
    
    for (const line of lines) {
      try {
        const result = parseAnkiQuestion(line, subject);
        if (!result) {
          const reason = 'Parse failure';
          rejectionReasons[reason] = (rejectionReasons[reason] || 0) + 1;
          errors.push({ line, error: 'Failed to parse question' });
          continue;
        }
        if (duplicateCheck.has(result.questionKey)) {
          const reason = 'Duplicate question';
          rejectionReasons[reason] = (rejectionReasons[reason] || 0) + 1;
          errors.push({ line, error: 'Duplicate question' });
          continue;
        }
        duplicateCheck.add(result.questionKey);

        if (subject === 'Current Affairs' && importDate) {
          const dateStr = importDate.toISOString().split('T')[0];
          currentAffairsQuestions.push({
            question: result.data.Question,
            option_a: result.data["Option A"],
            option_b: result.data["Option B"],
            option_c: result.data["Option C"],
            option_d: result.data["Option D"],
            correct_answer: result.data["Correct Answer"],
            explanation: result.data["Explanation"] || null,
            difficulty: result.data.Difficulty || 'medium',
            date: dateStr,
            time_period: timePeriod || 'daily',
            // Don't set created_by field at all
            metadata: { source: 'anki' }, // Store source in metadata as a proper JSON object
          });
        } else {
          questions.push(result.data);
        }
      } catch (error) {
        console.error('Error processing line:', error);
        const reason = 'Processing error';
        rejectionReasons[reason] = (rejectionReasons[reason] || 0) + 1;
        errors.push({ line, error: String(error) });
      }
    }
    
    let importedCount = 0;
    
    if (currentAffairsQuestions.length > 0) {
      const BATCH_SIZE = 50;
      for (let i = 0; i < currentAffairsQuestions.length; i += BATCH_SIZE) {
        const batch = currentAffairsQuestions.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from('current_affairs_questions').insert(batch);
        if (error) {
          console.error('Error inserting Current Affairs batch:', error);
          throw error;
        }
        importedCount += batch.length;
      }
    }
    
    if (questions.length > 0) {
      const BATCH_SIZE = 50;
      for (let i = 0; i < questions.length; i += BATCH_SIZE) {
        const batch = questions.slice(i, i + BATCH_SIZE);
        const { error } = await supabase.from('questions').insert(batch);
        if (error) {
          console.error('Error inserting questions:', error);
          throw error;
        }
        importedCount += batch.length;
      }
    }
    
    return { 
      importedCount, 
      errors, 
      totalProcessed: lines.length,
      rejectionReasons
    };
  } catch (error) {
    console.error('Import failed:', error);
    throw error;
  }
}
